import React, { useState } from 'react';

import { ComputedResponseDtoListActionsRequisesEnum } from 'lib_api/lib/api/gen';

import CollapsableInformationBanner from 'components/Banner/InformationBanner/CollapsableInformationBanner';
import { useRequiredActions } from 'hooks/comptes/useRequiredActions';
import DisplayedInformationMessageProvider from 'hooks/DisplayMessageContext';
import DossierActionsRequisesProvider from 'hooks/DossierActionsRequisesContext';
import RequiredUserActionProvider from 'hooks/RequiredUserActionContext';
import { useUserStore } from 'hooks/UserStoreContext';
import { profileEnumMapping } from 'utils/enumData';

import { DossierActionRequiseBanner } from './DossierActionRequiseBanner';
import { RequiredUserActionBanner } from './RequiredUserActionBanner';
import { useInformationMessageDisplay } from './useInformationMessageDisplay';
import { checkIfMessageIdIsInCollapsedMessagesIds } from './utils';

interface DisplayMessagesProps {
  children: React.ReactNode;
}

export const InformationMessagesDisplay = ({
  children,
}: DisplayMessagesProps): React.ReactElement => {
  const user = useUserStore();
  if (user.profile === null) {
    throw new Error('Authenticated user should be mapped to a profile');
  }

  const [areMessagesShown, setMessagesShown] = useState<boolean>(true);
  const [requiredUserActions, updateRequiredUserActions] = useRequiredActions();
  const [dossierActionsRequises, setDossierActionsRequises] = useState<
    ComputedResponseDtoListActionsRequisesEnum[]
  >([]);
  const { profilPath } = profileEnumMapping[user.profile];

  const { messages, addMessage, removeMessage } =
    useInformationMessageDisplay();

  return (
    <DisplayedInformationMessageProvider
      value={{
        messages,
        addMessage,
        removeMessage,
        setMessagesShown,
      }}
    >
      <RequiredUserActionProvider
        value={{ requiredUserActions, updateRequiredUserActions }}
      >
        <DossierActionsRequisesProvider
          value={{ dossierActionsRequises, setDossierActionsRequises }}
        >
          {areMessagesShown && (
            <RequiredUserActionBanner profilPath={profilPath} />
          )}
          {areMessagesShown && <DossierActionRequiseBanner />}
          {areMessagesShown &&
            Object.values(messages).map(({ id, title, content }) => (
              <CollapsableInformationBanner
                message={title}
                description={content}
                key={id}
                closable={true}
                onClose={() => removeMessage(id)}
                alertType={'info'}
                initialCollapseDescription={checkIfMessageIdIsInCollapsedMessagesIds(
                  id,
                )}
              />
            ))}
          {children}
        </DossierActionsRequisesProvider>
      </RequiredUserActionProvider>
    </DisplayedInformationMessageProvider>
  );
};
